/**
 * SQL 구조 스캐너(2026-10-19, SQL 빌더) — 텍스트에서 「질의 블록 트리」 를 만든다.
 *
 * 원본은 언제나 SQL 텍스트다. 여기서 만든 트리는 그 텍스트의 **위치(오프셋)** 를 가리킬 뿐이고, 빌더의 편집은 전부
 * 「그 범위의 텍스트를 바꾸고 → 다시 파싱」 이다(트리를 고쳐 SQL 을 다시 만들지 않는다 — 주석·힌트·서식이 그대로 남는다).
 * 의미 분석은 하지 않는다: 절 경계(최상위 괄호 깊이의 키워드)와 서브쿼리(괄호 안이 SELECT·WITH 로 시작)만 찾는다.
 * 그래서 문법이 조금 틀려도 트리는 나온다 — 짝이 안 맞는 괄호 같은 것은 errors 로 알린다.
 *
 * 노드 모양
 *   질의(Query): { id, kind, label, start, end, outerStart, outerEnd, clauses: Clause[], branches: Query[], ops: string[] }
 *     kind = main | cte | inline | scalar | exists | in | subquery | branch | insert
 *     start·end 는 질의 본문(괄호 안), outerStart·outerEnd 는 괄호까지
 *   절(Clause): { id, type, start, end, bodyStart, bodyEnd, items: [{start, end}], children: Query[] }
 *     type = WITH | SELECT | FROM | WHERE | GROUP BY | HAVING | ORDER BY | CONNECT BY | START WITH | OFFSET | FETCH | LIMIT | FOR UPDATE | WINDOW | STATEMENT
 *   id 는 경로다(q0/FROM/inline1) — 다시 파싱해도 같은 자리면 같은 id 라 선택·펼침이 유지된다.
 */
import { tokenize } from './tokenize';

/** 절 시작 키워드(여러 낱말이면 차례대로) — 긴 것부터 견준다 */
const CLAUSE_STARTS = [
    ['ORDER', 'SIBLINGS', 'BY'],
    ['GROUP', 'BY'], ['ORDER', 'BY'], ['CONNECT', 'BY'], ['START', 'WITH'], ['FOR', 'UPDATE'],
    ['SELECT'], ['FROM'], ['WHERE'], ['HAVING'], ['OFFSET'], ['FETCH'], ['LIMIT'], ['WINDOW'],
];
const CLAUSE_TYPE = (words) => (words[0] === 'ORDER' ? 'ORDER BY' : words.join(' '));
const SET_OPS = new Set(['UNION', 'INTERSECT', 'MINUS', 'EXCEPT']);
const JOIN_MODS = new Set(['INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS', 'NATURAL']);
/** 별칭으로 볼 수 없는 낱말 — 괄호 뒤에 오면 다음 구문이다 */
const NOT_ALIAS = new Set([
    'ON', 'USING', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS', 'NATURAL', 'WHERE', 'GROUP', 'ORDER', 'HAVING', 'CONNECT',
    'START', 'UNION', 'INTERSECT', 'MINUS', 'EXCEPT', 'AND', 'OR', 'NOT', 'FROM', 'SELECT', 'WITH', 'FETCH', 'OFFSET', 'LIMIT', 'FOR',
    'PIVOT', 'UNPIVOT', 'MODEL', 'WINDOW', 'IS', 'IN', 'BETWEEN', 'LIKE', 'THEN', 'ELSE', 'END', 'WHEN', 'APPLY', 'PARTITION', 'SAMPLE',
]);

const isWord = (tok, v) => !!tok && tok.t === 'word' && !tok.quoted && tok.v === v;
const isPunct = (tok, v) => !!tok && tok.t === 'punct' && tok.v === v;

export function parseSqlStructure(text) {
    const src = String(text || '');
    const { tokens: tok, errors: lexErrors } = tokenize(src);
    const errors = [...lexErrors];

    // 괄호 짝 — 짝이 없는 '(' 는 끝까지로 본다(트리는 나오게)
    const match = new Array(tok.length).fill(-1);
    const stack = [];
    tok.forEach((tk, i) => {
        if (isPunct(tk, '(')) stack.push(i);
        else if (isPunct(tk, ')')) {
            if (stack.length) match[stack.pop()] = i;
            else errors.push({ code: 'UNMATCHED_CLOSE', pos: tk.start });
        }
    });
    for (const i of stack) { match[i] = tok.length; errors.push({ code: 'UNMATCHED_OPEN', pos: tok[i].start }); }
    const closeOf = (i) => match[i];

    // 문장 끝 — 최상위의 ; 또는 / 까지만(빌더는 문장 하나를 다룬다)
    let end = tok.length;
    for (let i = 0; i < tok.length; i++) {
        if (isPunct(tok[i], '(')) { i = Math.min(closeOf(i), tok.length - 1); continue; }
        if (isPunct(tok[i], ';')) {
            end = i;
            if (tok.slice(i + 1).some(x => !isPunct(x, ';') && !isPunct(x, '/'))) errors.push({ code: 'MULTI_STATEMENT', pos: tok[i].start });
            break;
        }
    }
    while (end > 0 && isPunct(tok[end - 1], '/')) end--;

    const startOf = (i) => (tok[i] ? tok[i].start : src.length);
    const endOf = (i) => (tok[i] ? tok[i].end : 0);

    /** [lo, hi) 의 최상위(깊이 0) 토큰을 차례로 돈다 — 괄호 묶음은 여는 괄호 하나로 친다 */
    function* topLevel(lo, hi) {
        for (let i = lo; i < hi; i++) {
            yield i;
            if (isPunct(tok[i], '(')) i = Math.min(closeOf(i), hi);
        }
    }

    function clauseAt(i) {
        for (const words of CLAUSE_STARTS) {
            if (words.every((w, k) => isWord(tok[i + k], w))) return { type: CLAUSE_TYPE(words), len: words.length };
        }
        return null;
    }

    function kindFor(clauseType, prev) {
        if (isWord(prev, 'EXISTS')) return 'exists';
        if (isWord(prev, 'IN')) return 'in';   // NOT IN 도 바로 앞은 IN 이다
        if (isWord(prev, 'ANY') || isWord(prev, 'ALL') || isWord(prev, 'SOME')) return 'subquery';
        if (clauseType === 'SELECT') return 'scalar';
        if (clauseType === 'FROM') return 'inline';
        return 'subquery';
    }

    function aliasAfter(closeIdx, hi) {
        let a = closeIdx + 1;
        if (a >= hi) return null;
        if (isWord(tok[a], 'AS')) a++;
        const tk = tok[a];
        if (!tk || a >= hi || tk.t !== 'word') return null;
        if (!tk.quoted && NOT_ALIAS.has(tk.v)) return null;
        return src.slice(tk.start, tk.end);
    }

    /** 절(또는 문장) 안의 서브쿼리를 찾는다 — 괄호 안이 SELECT·WITH 면 질의, 아니면 그 안을 계속 본다(CASE·함수 인자 속 서브쿼리) */
    function scanChildren(clause, lo, hi) {
        const counter = {};
        for (let i = lo; i < hi; i++) {
            if (!isPunct(tok[i], '(')) continue;
            const m = Math.min(closeOf(i), hi);
            const first = tok[i + 1];
            if (i + 1 < m && (isWord(first, 'SELECT') || isWord(first, 'WITH'))) {
                const kind = kindFor(clause.type, tok[i - 1]);
                counter[kind] = (counter[kind] || 0) + 1;
                const child = parseQuery(i + 1, m, kind, `${clause.id}/${kind}${counter[kind]}`);
                child.outerStart = tok[i].start;
                child.outerEnd = m < tok.length ? tok[m].end : src.length;
                const alias = aliasAfter(m, hi);
                if (alias) child.label = alias;
                clause.children.push(child);
                i = m;
            }
        }
    }

    /** 절의 항목 — SELECT·GROUP BY·ORDER BY 는 쉼표, FROM 은 쉼표·JOIN, WHERE 류는 AND·OR(BETWEEN … AND 는 하나) */
    function splitItems(type, lo, hi) {
        const items = [];
        let from = lo;
        // SELECT DISTINCT 같은 앞머리는 항목에 넣지 않는다
        if (type === 'SELECT') while (from < hi && (isWord(tok[from], 'DISTINCT') || isWord(tok[from], 'UNIQUE') || isWord(tok[from], 'ALL'))) from++;
        const push = (a, b) => { if (b > a) items.push({ start: tok[a].start, end: tok[b - 1].end }); };
        let pendingBetween = false;
        for (const i of topLevel(from, hi)) {
            const tk = tok[i];
            if (type === 'WHERE' || type === 'HAVING' || type === 'CONNECT BY' || type === 'START WITH') {
                if (isWord(tk, 'BETWEEN')) pendingBetween = true;
                if (isWord(tk, 'AND') && pendingBetween) { pendingBetween = false; continue; }
                if (isWord(tk, 'AND') || isWord(tk, 'OR')) { push(from, i); from = i + 1; }
                continue;
            }
            if (isPunct(tk, ',')) { push(from, i); from = i + 1; continue; }
            if (type === 'FROM' && (isWord(tk, 'JOIN') || isWord(tk, 'APPLY'))) {
                let s = i;
                while (s - 1 >= from && tok[s - 1].t === 'word' && JOIN_MODS.has(tok[s - 1].v)) s--;
                if (s > from) { push(from, s); from = s; }
            }
        }
        push(from, hi);
        return items;
    }

    function makeClause(q, type, kwLo, bodyLo, bodyHi, seen) {
        seen[type] = (seen[type] || 0) + 1;
        const clause = {
            id: `${q.id}/${type}${seen[type] > 1 ? `#${seen[type]}` : ''}`,
            type,
            start: startOf(kwLo),
            end: bodyHi > kwLo ? endOf(bodyHi - 1) : endOf(kwLo),
            bodyStart: bodyLo < bodyHi ? startOf(bodyLo) : endOf(bodyLo - 1),
            bodyEnd: bodyHi > bodyLo ? endOf(bodyHi - 1) : endOf(bodyLo - 1),
            items: [],
            children: [],
        };
        if (type !== 'STATEMENT') clause.items = splitItems(type, bodyLo, bodyHi);
        scanChildren(clause, bodyLo, bodyHi);
        return clause;
    }

    function parseQuery(lo, hi, kind, id) {
        const q = {
            id, kind, label: null,
            start: startOf(lo), end: hi > lo ? endOf(hi - 1) : startOf(lo),
            outerStart: startOf(lo), outerEnd: hi > lo ? endOf(hi - 1) : startOf(lo),
            clauses: [], branches: [], ops: [],
        };
        if (hi <= lo) return q;
        const seen = {};
        let i = lo;

        // 통째로 괄호에 싸인 질의 — ( SELECT … )
        if (isPunct(tok[lo], '(') && closeOf(lo) === hi - 1 && (isWord(tok[lo + 1], 'SELECT') || isWord(tok[lo + 1], 'WITH'))) {
            const inner = parseQuery(lo + 1, hi - 1, kind, id);
            return { ...inner, outerStart: q.outerStart, outerEnd: q.outerEnd };
        }

        // WITH 이름 [ (컬럼) ] AS [ [NOT] MATERIALIZED ] ( 질의 ) , …
        if (isWord(tok[i], 'WITH')) {
            const withLo = i;
            let j = i + 1;
            if (isWord(tok[j], 'RECURSIVE')) j++;
            const ctes = [];
            while (j < hi && tok[j] && tok[j].t === 'word') {
                const nameTok = tok[j];
                j++;
                if (isPunct(tok[j], '(') && !(isWord(tok[j + 1], 'SELECT') || isWord(tok[j + 1], 'WITH'))) j = closeOf(j) + 1;
                if (!isWord(tok[j], 'AS')) break;
                j++;
                if (isWord(tok[j], 'NOT')) j++;
                if (isWord(tok[j], 'MATERIALIZED')) j++;
                if (!isPunct(tok[j], '(')) break;
                const m = Math.min(closeOf(j), hi);
                const name = src.slice(nameTok.start, nameTok.end);
                const cte = parseQuery(j + 1, m, 'cte', `${id}/WITH/${name}`);
                cte.label = name;
                cte.outerStart = nameTok.start;
                cte.outerEnd = m < tok.length ? tok[m].end : src.length;
                ctes.push(cte);
                j = m + 1;
                // SEARCH … / CYCLE … (재귀 CTE 꼬리) 는 다음 쉼표·본문 전까지 건너뛴다
                while (j < hi && !isPunct(tok[j], ',') && !isWord(tok[j], 'SELECT') && !isPunct(tok[j], '(')) j++;
                if (isPunct(tok[j], ',')) { j++; continue; }
                break;
            }
            const clause = {
                id: `${id}/WITH`, type: 'WITH',
                start: startOf(withLo), end: endOf(j - 1),
                bodyStart: startOf(withLo + 1), bodyEnd: endOf(j - 1),
                items: ctes.map(c => ({ start: c.outerStart, end: c.outerEnd })),
                children: ctes,
            };
            q.clauses.push(clause);
            i = j;
        }

        // 집합 연산 — 최상위 UNION / INTERSECT / MINUS / EXCEPT 로 가지를 나눈다
        const cuts = [];
        for (const k of topLevel(i, hi)) {
            if (tok[k] && tok[k].t === 'word' && !tok[k].quoted && SET_OPS.has(tok[k].v)) {
                const len = (isWord(tok[k + 1], 'ALL') || isWord(tok[k + 1], 'DISTINCT')) ? 2 : 1;
                cuts.push({ at: k, len, op: tok.slice(k, k + len).map(x => x.v).join(' ') });
            }
        }
        if (cuts.length) {
            let a = i;
            const ranges = [];
            for (const c of cuts) { ranges.push([a, c.at]); a = c.at + c.len; }
            ranges.push([a, hi]);
            ranges.forEach(([ra, rb], n) => {
                const br = parseQuery(ra, rb, 'branch', `${id}/b${n + 1}`);
                if (n > 0) br.label = cuts[n - 1].op;
                q.branches.push(br);
            });
            q.ops = cuts.map(c => c.op);
            return q;
        }

        // ( SELECT … ) ORDER BY … — 괄호 질의 뒤에 절이 더 붙은 가지(UNION 의 마지막 가지에서 흔하다)
        if (isPunct(tok[i], '(') && (isWord(tok[i + 1], 'SELECT') || isWord(tok[i + 1], 'WITH')) && closeOf(i) < hi - 1) {
            const m = closeOf(i);
            const inner = parseQuery(i + 1, m, kind, id);
            const tailSeen = {};
            inner.clauses.forEach(c => { tailSeen[c.type] = (tailSeen[c.type] || 0) + 1; });
            const tail = [];
            for (const k of topLevel(m + 1, hi)) { const c = clauseAt(k); if (c) tail.push({ at: k, ...c }); }
            tail.forEach((c, n) => {
                const next = n + 1 < tail.length ? tail[n + 1].at : hi;
                inner.clauses.push(makeClause(inner, c.type, c.at, c.at + c.len, next, tailSeen));
            });
            return { ...inner, label: q.label, start: inner.start, end: q.end, outerStart: q.outerStart, outerEnd: q.outerEnd };
        }

        // SELECT 가 아닌 문장(INSERT·UPDATE·DELETE·MERGE …) — 한 덩어리 + 안의 서브쿼리. INSERT … SELECT 의 SELECT 는 질의로
        if (!isWord(tok[i], 'SELECT')) {
            let selAt = -1;
            if (isWord(tok[lo], 'INSERT')) {
                for (const k of topLevel(i, hi)) if (isWord(tok[k], 'SELECT') || isWord(tok[k], 'WITH')) { selAt = k; break; }
            }
            const stHi = selAt >= 0 ? selAt : hi;
            if (stHi > i) q.clauses.push(makeClause(q, 'STATEMENT', i, i, stHi, seen));
            if (selAt >= 0) {
                const sub = parseQuery(selAt, hi, 'insert', `${id}/insert1`);
                if (q.clauses[0]) q.clauses[0].children.push(sub);
                else q.branches.push(sub);
            }
            return q;
        }

        // SELECT … FROM … WHERE … — 최상위 절 키워드로 나눈다
        const starts = [];
        for (const k of topLevel(i, hi)) {
            const c = clauseAt(k);
            if (!c) continue;
            if (c.type === 'SELECT' && starts.length) continue;   // 두 번째 SELECT 는 절이 아니다(INSERT ALL 따위)
            starts.push({ at: k, ...c });
        }
        starts.forEach((c, n) => {
            const next = n + 1 < starts.length ? starts[n + 1].at : hi;
            q.clauses.push(makeClause(q, c.type, c.at, c.at + c.len, next, seen));
        });
        return q;
    }

    const root = end > 0 ? parseQuery(0, end, 'main', 'q0') : null;
    return { root, errors, length: src.length };
}

/** 트리를 깊이 우선으로 — 질의와 절 모두 */
export function walk(root, fn) {
    if (!root) return;
    const visitQuery = (q, depth, parent) => {
        fn(q, 'query', depth, parent);
        for (const c of q.clauses) {
            fn(c, 'clause', depth, q);
            for (const ch of c.children) visitQuery(ch, depth + 1, c);
        }
        for (const b of q.branches) visitQuery(b, depth + 1, q);
    };
    visitQuery(root, 0, null);
}

export function findNode(root, id) {
    let hit = null;
    walk(root, (n) => { if (!hit && n.id === id) hit = n; });
    return hit;
}

/** 오프셋을 품은 가장 깊은 질의(편집기 커서 → 아웃라인 선택) */
export function deepestQueryAt(root, offset) {
    let hit = null;
    walk(root, (n, type, depth) => {
        if (type === 'query' && offset >= n.outerStart && offset <= n.outerEnd && (!hit || depth >= hit.depth)) hit = { node: n, depth };
    });
    return hit ? hit.node : null;
}
